import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Lobby from "@/components/lobby/Lobby";
import LandingPage from "@/components/landing/LandingPage";
import PlannerDashboard from "@/components/planner/PlannerDashboard";
import DriverDashboard from "@/components/driver/DriverDashboard";
import MachinistDashboard from "@/components/machinist/MachinistDashboard";
import ViewerDashboard from "@/components/shared/ViewerDashboard";

export const AppContext = createContext(null);

export function useAppContext() {
  return useContext(AppContext);
}

function App() {
  const [session, setSession] = useState(() => {
    const saved = localStorage.getItem("pigeon-session");
    return saved ? JSON.parse(saved) : null;
  });

  const [hubData, setHubData] = useState({ sites: [], machines: [], drivers: [], requests: [], events: [], messages: [], feedback: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem("pigeon-session", JSON.stringify(session));
  }, [session]);

  const refreshData = async () => {
    if (!session?.hubId) return;
    setLoading(true);
    try {
      const [sites, machines, drivers, requests, events, messages, feedback] = await Promise.all([
        api.getSites(session.hubId),
        api.getMachines(session.hubId),
        api.getDrivers(session.hubId),
        api.getRequests(session.hubId),
        api.getEvents(session.hubId, 100),
        api.getMessages(session.hubId),
        api.getFeedback(session.hubId),
      ]);
      setHubData({ sites, machines, drivers, requests, events, messages, feedback });
    } catch (e) {
      console.error("Data refresh error:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (session?.hubId) {
      refreshData();
      const interval = setInterval(refreshData, 15000);
      return () => clearInterval(interval);
    }
  }, [session?.hubId]);

  const logout = () => {
    setSession(null);
    localStorage.removeItem("pigeon-session");
  };

  const ctxValue = {
    session, setSession, hubData, refreshData, loading, logout, setHubData
  };

  return (
    <AppContext.Provider value={ctxValue}>
      <div className="App dark">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Lobby />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/success" element={<LandingPage />} />
            <Route path="/planner" element={session?.role === "planner" ? <PlannerDashboard /> : <Navigate to="/" />} />
            <Route path="/driver/:driverName" element={session?.role === "driver" ? <DriverDashboard /> : <Navigate to="/" />} />
            <Route path="/driver" element={session?.role === "driver" ? <DriverDashboard /> : <Navigate to="/" />} />
            <Route path="/machinist" element={session?.role === "machinist" ? <MachinistDashboard /> : <Navigate to="/" />} />
            <Route path="/viewer" element={session?.role === "viewer" ? <ViewerDashboard /> : <Navigate to="/" />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AppContext.Provider>
  );
}

export default App;
